import { Component } from '@theme/component';
import { WishlistStore, WishlistUpdateEvent } from '@theme/wishlist';

class WishlistToggleButton extends Component {
  requiredRefs = ['button', 'heartEmpty', 'heartFilled'];

  get productId() {
    return this.dataset.productId ?? '';
  }

  get productHandle() {
    return this.dataset.productHandle ?? '';
  }

  get productTitle() {
    return this.dataset.productTitle ?? '';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.#onUpdate);
    // The PLP variant strip repoints this button at a sibling product when the shopper
    // switches print (see `#retargetCart` in product-card.js). The filled/empty state is
    // per-product, so it has to be re-read from the store whenever that happens — otherwise
    // the heart keeps showing the previous print's state.
    this.addEventListener('wishlist:resync', this.#onResync);
    this.#syncState(WishlistStore.hasItem(this.productId));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.#onUpdate);
    this.removeEventListener('wishlist:resync', this.#onResync);
  }

  #onResync = () => {
    this.#syncState(WishlistStore.hasItem(this.productId));
  };

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.productId) return;
    const list = WishlistStore.getDefaultList();
    if (WishlistStore.hasItemInList(list.id, this.productId)) {
      WishlistStore.removeItem(list.id, this.productId);
    } else {
      WishlistStore.addItem(list.id, this.productId, this.productHandle);
      document.dispatchEvent(new CustomEvent('wishlist:toast', {
        detail: {
          productId: String(this.productId),
          productHandle: this.productHandle,
          productTitle: this.productTitle,
          listId: list.id,
          listName: list.name,
        },
      }));
    }
  }

  #onUpdate = ({ detail }) => {
    // `sync` and `clear` are store-wide resets (e.g. sign-out clearing local
    // data), not tied to a single product — always re-read state for those.
    const storeWide = detail.action === 'sync' || detail.action === 'clear';
    if (detail.productId !== this.productId && !storeWide) return;
    this.#syncState(WishlistStore.hasItem(this.productId));
  };

  #syncState(isWishlisted) {
    this.refs.heartEmpty.classList.toggle('hidden', isWishlisted);
    this.refs.heartFilled.classList.toggle('hidden', !isWishlisted);
    // State goes on the <button> so screen readers announce it; the wrapper only
    // carries the CSS hook (it has no role, so ARIA on it is inert).
    this.refs.button.setAttribute('aria-pressed', String(isWishlisted));
    this.setAttribute('data-wishlisted', String(isWishlisted));
    this.refs.tooltipAdd?.classList.toggle('hidden', isWishlisted);
    this.refs.tooltipRemove?.classList.toggle('hidden', !isWishlisted);
  }
}

if (!customElements.get('wishlist-toggle-button')) {
  customElements.define('wishlist-toggle-button', WishlistToggleButton);
}
